import React, { useContext } from "react";
import moment from "moment-timezone";
import { formatDateString } from "../../../../utils";
import LanguageContext from "../../../../utils/Contexts/LanguageContext";

import "./style.css";

const ContractTimeline = ({ timeLine }) => {
  let previousDate = null;
  const { t } = useContext(LanguageContext);

  const handleTimeStatus = (toShow, item) => {
    if (!toShow || !item) return;

    // Converts UTC string to local moment and logs all info
    const localTimeFromUTC = (utcStr) => {
      const localTime = moment.utc(utcStr).local(); // auto handles DST
      return localTime;
    };

    switch (toShow) {
      case 1: {
        const startDate = localTimeFromUTC(item?.start, "Start Date");
        const currentDate = moment();

        const timeDiff = startDate.diff(currentDate);
        const duration = moment.duration(Math.abs(timeDiff));

        if (timeDiff < 0) {
          if (duration.days() > 0)
            return `${duration.days()} ${t("day")}${
              duration.days() > 1 ? "s" : ""
            } ${t("ago")}`;
          if (duration.hours() > 0)
            return `${duration.hours()} ${t("hour")}${
              duration.hours() > 1 ? "s" : ""
            } ${t("ago")}`;
          return `${duration.seconds()} ${t("second")}${
            duration.seconds() > 1 ? "s" : ""
          } ${t("ago")}`;
        } else {
          if (duration.days() > 0)
            return `${t("active next")} ${duration.days()} ${t("day")}${
              duration.days() > 1 ? "s" : ""
            }`;
          if (duration.hours() > 0)
            return `${t("active next")} ${duration.hours()} ${t("hour")}${
              duration.hours() > 1 ? "s" : ""
            }`;
          if (duration.minutes() > 0)
            return `${t("active next")} ${duration.minutes()} ${t("minute")}${
              duration.minutes() > 1 ? "s" : ""
            }`;
          return `${t("active next")} ${duration.seconds()} ${t("second")}${
            duration.seconds() > 1 ? "s" : ""
          }`;
        }
      }

      case 2: {
        const endDate = localTimeFromUTC(item?.end, "End Date");
        const currentDate = moment();

        const timeRemaining = endDate.diff(currentDate);
        const duration = moment.duration(timeRemaining);

        if (timeRemaining < 0) {
          return `${t("Ended")} ${moment
            .duration(Math.abs(timeRemaining))
            .humanize()} ${t("ago")}`;
        }

        if (duration.years() > 0)
          return `${t("Ends in")} ${duration.years()} ${t("year")}${
            duration.years() > 1 ? "s" : ""
          }`;
        if (duration.days() > 0)
          return `${t("Ends in")} ${duration.days()} ${t("day")}${
            duration.days() > 1 ? "s" : ""
          }`;
        if (duration.hours() > 0)
          return `${t("Ends in")} ${duration.hours()} ${t("hour")}${
            duration.hours() > 1 ? "s" : ""
          }`;
        if (duration.minutes() > 0)
          return `${t("Ends in")} ${duration.minutes()} ${t("minute")}${
            duration.minutes() > 1 ? "s" : ""
          }`;
        return `${t("Ends in")} ${duration.seconds()} ${t("second")}${
          duration.seconds() > 1 ? "s" : ""
        }`;
      }

      case 3: {
        const start = localTimeFromUTC(item?.start, "Start (case 3)");
        const currentDate = moment();

        const isStarted = start.isBefore(currentDate);
        const diff = moment.duration(Math.abs(currentDate.diff(start)));

        const totalDays = Math.floor(diff.asDays());
        const totalHours = Math.floor(diff.asHours());
        const totalMinutes = Math.floor(diff.asMinutes());
        const totalSeconds = Math.floor(diff.asSeconds());

        if (isStarted) {
          if (totalDays > 0) return `${totalDays} ${t("days")} ${t("ago")}`;
          if (totalHours > 0) return `${totalHours} ${t("hours")} ${t("ago")}`;
          if (totalMinutes > 0)
            return `${totalMinutes} ${t("minutes")} ${t("ago")}`;
          return `${totalSeconds} ${t("seconds")} ${t("ago")}`;
        } else {
          if (totalDays > 0)
            return `${t("active next")} ${totalDays} ${t("days")}`;
          if (totalHours > 0)
            return `${t("active next")} ${totalHours} ${t("hours")}`;
          if (totalMinutes > 0)
            return `${t("active next")} ${totalMinutes} ${t("minutes")}`;
          return `${t("active next")} ${totalSeconds} ${t("seconds")}`;
        }
      }

      case 4: {
        const startTime = localTimeFromUTC(item?.start, "Progress Start Time");
        const endTime = localTimeFromUTC(item?.end, "Progress End Time");

        const now = moment();
        const duration = now.diff(startTime, "days"); // how many days have passed

        const max = moment(endTime).diff(startTime, "days"); // total duration in days
        const progressPercentage = (duration / max) * 100;
        const clampedProgress = Math.min(100, Math.max(0, progressPercentage));

        return (
          <div
            className="progress timline-progress-text"
            style={{
              height: "20px",
              position: "relative",
              marginTop: "10px",
              borderRadius: "5px",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#ffffff",
                zIndex: 1,
              }}
            >
              {duration} / {max} {t("days")}
            </div>
            <div
              className="progress-bar"
              role="progressbar"
              style={{ width: `${clampedProgress}%` }}
              aria-valuenow={clampedProgress}
              aria-valuemin={0}
              aria-valuemax={max}
            />
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <main className="timeline_main">
      <ul className="step">
        {timeLine &&
          timeLine.length >= 1 &&
          timeLine
            .slice()
            .reverse()
            .map((item, index) => {
              const currentDate = formatDateString(item?.start, "date");

              // Check if current date is different from the previous date
              const renderDate = currentDate !== previousDate;
              previousDate = currentDate;
              const activeIndex = timeLine?.findIndex((item) => item.active);

              const currentOriginalIndex = timeLine.length - 1 - index;

              // Item is "previously active" if it comes before the active item
              const isPreviousActive = currentOriginalIndex < activeIndex;

              return (
                <>
                  {renderDate && (
                    <li className="step-item" key={index}>
                      <div className="step-content-wrapper">
                        <small className="step-divider">{currentDate}</small>
                      </div>
                    </li>
                  )}
                  <li className="step-item" key={index + 1000}>
                    <div className="step-content-wrapper">
                      <div
                        className={
                          item?.active
                            ? "step-avatar step-icon-primary" // Class for current active item
                            : isPreviousActive
                            ? "step-avatar step-icon-secondary" // Class for previous active item
                            : "step-avatar" // Class for non-active item
                        }
                        style={{ fontSize: "12px", fontWeight: "700" }}
                      >
                        {timeLine?.length - index}
                      </div>
                      <div className="step-content">
                        <h5 className="mb-1">{t(item?.type?.label || "")}</h5>
                        <div
                          className="font-size-sm"
                          style={{
                            color: "#919293",
                          }}
                        >
                          {handleTimeStatus(item?.show?.id, item)}
                        </div>
                      </div>
                    </div>
                  </li>
                </>
              );
            })}
      </ul>
    </main>
  );
};

export default ContractTimeline;
